import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceDot,
  BarChart,
  Bar,
} from "recharts";
import {
  ArrowDown,
  ArrowUp,
  ArrowRight,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  Info,
  ChevronDown,
  ChevronUp,
  Target,
  Trophy,
  Ban,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import logoImg from "@/assets/logo.png";
import {
  CAMPAIGN_PERIODS,
  TOTAL_PERIODS,
  AUCTION_INSIGHTS,
  aggregateTotals,
  weightedImprShare,
  filterByCampaign,
  periodsInRange,
  fmtEur,
  fmtNumber,
  fmtPct,
  fmtDateDe,
  type CampaignName,
} from "@/lib/reporting-data";

export const Route = createFileRoute("/reporting")({
  head: () => ({
    meta: [
      { title: "Google Ads Reporting — Schneider Bodenbeschichtungen" },
      { name: "description", content: "Owner-Reporting Dashboard für Google Ads Kampagnen." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ReportingDashboard,
});

const CAMPAIGN_COLORS: Record<CampaignName, string> = {
  Industriebodenbeschichtung: "hsl(142 71% 40%)", // green
  Tiefgarage: "hsl(215 20% 55%)", // grey
};

const TREND_UP_GOOD = "text-emerald-600";
const TREND_DOWN_GOOD = "text-emerald-600";
const TREND_BAD = "text-red-600";
const TREND_NEUTRAL = "text-muted-foreground";

// ---------- Small building blocks ----------

function KpiCard({
  label,
  value,
  hint,
  tone = "neutral",
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "neutral" | "positive" | "warning";
}) {
  const toneClass =
    tone === "positive"
      ? "border-emerald-200 bg-emerald-50/50"
      : tone === "warning"
        ? "border-amber-200 bg-amber-50/50"
        : "";
  return (
    <Card className={toneClass}>
      <CardContent className="pt-5">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
        {hint ? <div className="mt-1 text-xs text-muted-foreground">{hint}</div> : null}
      </CardContent>
    </Card>
  );
}

function TrendPill({
  current,
  previous,
  lowerIsBetter = false,
  isPercentagePoints = false,
  formatter,
}: {
  current: number | null;
  previous: number | null;
  lowerIsBetter?: boolean;
  isPercentagePoints?: boolean;
  formatter: (n: number) => string;
}) {
  if (current === null && previous === null) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  if (previous === null || previous === 0) {
    if (current && current > 0) {
      return <span className={`text-xs font-medium ${lowerIsBetter ? TREND_BAD : TREND_UP_GOOD}`}>von 0 auf {formatter(current)}</span>;
    }
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  if (current === null) {
    return <span className="text-xs text-muted-foreground">nur Vergleich</span>;
  }
  const diff = current - previous;
  const pct = (diff / Math.abs(previous)) * 100;
  const better = lowerIsBetter ? diff < 0 : diff > 0;
  const same = diff === 0;
  const cls = same ? TREND_NEUTRAL : better ? TREND_UP_GOOD : TREND_BAD;
  const Icon = same ? ArrowRight : diff > 0 ? ArrowUp : ArrowDown;
  const diffStr = isPercentagePoints ? `${diff > 0 ? "+" : ""}${diff.toLocaleString("de-DE", { maximumFractionDigits: 2 })} pp` : formatter(Math.abs(diff));
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${cls}`}>
      <Icon className="h-3 w-3" />
      {diff > 0 && !isPercentagePoints ? "+" : ""}
      {isPercentagePoints ? diffStr : (diff < 0 ? "-" : "") + diffStr.replace(/^-/, "")}
      <span className="text-muted-foreground">({pct > 0 ? "+" : ""}{pct.toLocaleString("de-DE", { maximumFractionDigits: 1 })}%)</span>
    </span>
  );
}

function SectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-4">
      <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
      {subtitle ? <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p> : null}
    </div>
  );
}

// ---------- Section 1: Executive Summary ----------

function ExecutiveSummary() {
  const fullPhase = useMemo(() => aggregateTotals(TOTAL_PERIODS.filter((p) => !p.is_partial)), []);
  const initialPhase = useMemo(
    () => aggregateTotals(TOTAL_PERIODS.filter((p) => !p.is_partial && p.period_end <= "2026-06-06")),
    [],
  );
  const improvedPhase = useMemo(
    () => aggregateTotals(TOTAL_PERIODS.filter((p) => !p.is_partial && p.period_start >= "2026-06-07")),
    [],
  );

  const wSIS = useMemo(
    () => weightedImprShare(CAMPAIGN_PERIODS.filter((r) => !r.is_partial)),
    [],
  );

  return (
    <section id="summary">
      <SectionHeader
        title="Executive Summary"
        subtitle="Vollständiger Zeitraum 05.05.2026 – 27.06.2026 (Anlauf- und verbesserte Phase). Teilzeitraum 28.06.–30.06. wird separat ausgewiesen."
      />

      <Alert className="mb-4 border-emerald-200 bg-emerald-50/60">
        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
        <AlertTitle className="text-emerald-900">Kernaussage für den Inhaber</AlertTitle>
        <AlertDescription className="mt-2 text-sm leading-relaxed text-emerald-950">
          Die Kampagne hat sich nach der Anlaufphase deutlich verbessert. In der{" "}
          <strong>Anlaufphase vom 05.05.–06.06.</strong> wurden 2 Conversions zu einem CPA von{" "}
          <strong>{fmtEur(588.06)}</strong> erzielt. In der <strong>verbesserten Phase vom 07.06.–27.06.</strong>{" "}
          wurden 8 Conversions zu einem CPA von <strong>{fmtEur(117.29)}</strong> erzielt — eine
          Effizienzsteigerung von rund <strong>80&nbsp;%</strong>. Alle Leads kamen aus der Kampagne{" "}
          <strong>Industriebodenbeschichtung</strong>. Die Kampagne <strong>Tiefgarage</strong> hat keine
          Leads generiert und sollte aktuell kein eigenes Budget erhalten.
        </AlertDescription>
      </Alert>

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        <KpiCard label="Kosten" value={fmtEur(fullPhase.cost_eur)} />
        <KpiCard label="Klicks" value={fmtNumber(fullPhase.clicks)} />
        <KpiCard label="Impressionen" value={fmtNumber(fullPhase.impressions)} />
        <KpiCard label="Conversions" value={fmtNumber(fullPhase.conversions)} tone="positive" />
        <KpiCard label="Conversion Rate" value={fmtPct(fullPhase.conv_rate_pct)} />
        <KpiCard label="CPA" value={fmtEur(fullPhase.cpa_eur)} tone="positive" />
        <KpiCard label="Search Impression Share" value={fmtPct(wSIS)} hint="impressionsgewichtet" />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <PhaseCard title="Anlaufphase" period="05.05.2026 – 06.06.2026" totals={initialPhase} tone="warning" />
        <PhaseCard title="Verbesserte Phase" period="07.06.2026 – 27.06.2026" totals={improvedPhase} tone="positive" />
      </div>
    </section>
  );
}

function PhaseCard({
  title,
  period,
  totals,
  tone,
}: {
  title: string;
  period: string;
  totals: ReturnType<typeof aggregateTotals>;
  tone: "positive" | "warning";
}) {
  const border = tone === "positive" ? "border-emerald-200" : "border-amber-200";
  const badgeCls =
    tone === "positive" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800";
  return (
    <Card className={border}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{title}</CardTitle>
          <Badge className={badgeCls} variant="secondary">
            {tone === "positive" ? <TrendingUp className="mr-1 h-3 w-3" /> : <AlertTriangle className="mr-1 h-3 w-3" />}
            {tone === "positive" ? "Deutlich effizienter" : "Anlaufphase"}
          </Badge>
        </div>
        <CardDescription>{period}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
          <MiniStat label="Kosten" value={fmtEur(totals.cost_eur)} />
          <MiniStat label="Klicks" value={fmtNumber(totals.clicks)} />
          <MiniStat label="Impr." value={fmtNumber(totals.impressions)} />
          <MiniStat label="Conversions" value={fmtNumber(totals.conversions)} />
          <MiniStat label="CPA" value={fmtEur(totals.cpa_eur)} />
          <MiniStat label="Conv.-Rate" value={fmtPct(totals.conv_rate_pct)} />
        </div>
      </CardContent>
    </Card>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-base font-semibold tabular-nums">{value}</div>
    </div>
  );
}

// ---------- Section 2: Period Comparison ----------

type CampaignFilter = "Alle" | CampaignName;

function PeriodComparison() {
  const [current, setCurrent] = useState<string>("2026-06-21|2026-06-27");
  const [previous, setPrevious] = useState<string>("2026-06-14|2026-06-20");
  const [campaign, setCampaign] = useState<CampaignFilter>("Alle");
  const [onlyComplete, setOnlyComplete] = useState(true);
  const [showCsvNote, setShowCsvNote] = useState(true);

  const periodOptions = useMemo(() => {
    const uniq = new Set<string>();
    for (const r of CAMPAIGN_PERIODS) {
      if (onlyComplete && r.is_partial) continue;
      uniq.add(`${r.period_start}|${r.period_end}|${r.is_partial ? "1" : "0"}`);
    }
    return Array.from(uniq).sort();
  }, [onlyComplete]);

  const [currentTotals, previousTotals, currentLabel, previousLabel] = useMemo(() => {
    const parse = (v: string) => {
      const [s, e] = v.split("|");
      return { s, e };
    };
    const { s: cs, e: ce } = parse(current);
    const { s: ps, e: pe } = parse(previous);
    const curRows = filterByCampaign(periodsInRange(CAMPAIGN_PERIODS, cs, ce), campaign);
    const prevRows = filterByCampaign(periodsInRange(CAMPAIGN_PERIODS, ps, pe), campaign);
    const cur = aggregateTotals(curRows);
    const prev = aggregateTotals(prevRows);
    const curSIS = weightedImprShare(curRows);
    const prevSIS = weightedImprShare(prevRows);
    return [
      { ...cur, sis: curSIS },
      { ...prev, sis: prevSIS },
      `${fmtDateDe(cs)} – ${fmtDateDe(ce)}`,
      `${fmtDateDe(ps)} – ${fmtDateDe(pe)}`,
    ];
  }, [current, previous, campaign]);

  const rowsForChart = useMemo(() => {
    const totals = TOTAL_PERIODS.map((p) => {
      const camp =
        campaign === "Alle"
          ? aggregateTotals(CAMPAIGN_PERIODS.filter((r) => r.period_start === p.period_start && r.period_end === p.period_end))
          : aggregateTotals(CAMPAIGN_PERIODS.filter((r) => r.period_start === p.period_start && r.period_end === p.period_end && r.campaign === campaign));
      const sis = weightedImprShare(
        CAMPAIGN_PERIODS.filter(
          (r) =>
            r.period_start === p.period_start &&
            r.period_end === p.period_end &&
            (campaign === "Alle" || r.campaign === campaign),
        ),
      );
      const lostB = (() => {
        const rows = CAMPAIGN_PERIODS.filter(
          (r) =>
            r.period_start === p.period_start &&
            r.period_end === p.period_end &&
            (campaign === "Alle" || r.campaign === campaign) &&
            r.lost_is_budget_pct !== null &&
            r.impressions > 0,
        );
        const impr = rows.reduce((s, r) => s + r.impressions, 0);
        if (impr === 0) return null;
        return rows.reduce((s, r) => s + (r.lost_is_budget_pct as number) * r.impressions, 0) / impr;
      })();
      const lostR = (() => {
        const rows = CAMPAIGN_PERIODS.filter(
          (r) =>
            r.period_start === p.period_start &&
            r.period_end === p.period_end &&
            (campaign === "Alle" || r.campaign === campaign) &&
            r.lost_is_rank_pct !== null &&
            r.impressions > 0,
        );
        const impr = rows.reduce((s, r) => s + r.impressions, 0);
        if (impr === 0) return null;
        return rows.reduce((s, r) => s + (r.lost_is_rank_pct as number) * r.impressions, 0) / impr;
      })();
      return {
        label: fmtDateDe(p.period_start).slice(0, 5),
        period_start: p.period_start,
        is_partial: p.is_partial,
        cost: camp.cost_eur,
        conv: camp.conversions,
        cpa: camp.cpa_eur,
        cr: camp.conv_rate_pct,
        sis,
        lostB,
        lostR,
      };
    });
    return totals;
  }, [campaign]);

  return (
    <section id="periods">
      <SectionHeader title="Wochen- / Zeitraum-Vergleich" subtitle="Vergleiche einen beliebigen Zeitraum mit einem früheren Zeitraum. Kennzahlen werden aus Summen neu berechnet (keine Mittelwerte über Kampagnen)." />

      <Card className="mb-4">
        <CardContent className="grid gap-4 pt-5 md:grid-cols-2 xl:grid-cols-4">
          <div>
            <Label className="mb-1.5 block text-xs">Aktueller Zeitraum</Label>
            <Select value={current} onValueChange={setCurrent}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {periodOptions.map((o) => {
                  const [s, e, p] = o.split("|");
                  return (
                    <SelectItem key={o} value={`${s}|${e}`}>
                      {fmtDateDe(s)} – {fmtDateDe(e)}{p === "1" ? " · Teilzeitraum" : ""}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block text-xs">Vergleichszeitraum</Label>
            <Select value={previous} onValueChange={setPrevious}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {periodOptions.map((o) => {
                  const [s, e, p] = o.split("|");
                  return (
                    <SelectItem key={o} value={`${s}|${e}`}>
                      {fmtDateDe(s)} – {fmtDateDe(e)}{p === "1" ? " · Teilzeitraum" : ""}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block text-xs">Kampagne</Label>
            <Tabs value={campaign} onValueChange={(v) => setCampaign(v as CampaignFilter)}>
              <TabsList className="grid h-auto w-full grid-cols-3">
                <TabsTrigger value="Alle" className="text-xs">Alle</TabsTrigger>
                <TabsTrigger value="Industriebodenbeschichtung" className="text-xs">Industrieboden</TabsTrigger>
                <TabsTrigger value="Tiefgarage" className="text-xs">Tiefgarage</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="flex flex-col justify-center gap-2">
            <div className="flex items-center gap-2">
              <Switch id="only-complete" checked={onlyComplete} onCheckedChange={setOnlyComplete} />
              <Label htmlFor="only-complete" className="text-xs">Nur vollständige Wochen anzeigen</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch id="csv-note" checked={showCsvNote} onCheckedChange={setShowCsvNote} />
              <Label htmlFor="csv-note" className="text-xs">CSV-Zeiträume-Hinweis anzeigen</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {showCsvNote ? (
        <Alert className="mb-4">
          <Info className="h-4 w-4" />
          <AlertTitle>CSV-Zeitraum-Hinweis</AlertTitle>
          <AlertDescription className="text-sm">
            Die aktuellen Zeiträume stammen aus aggregierten Google-Ads-CSV-Exporten und sind keine exakten Montag–Sonntag-Kalenderwochen.
            Nach Import von Tagesdaten werden echte Kalenderwochen (Montag 00:00 CET/CEST bis nächsten Montag 00:00) automatisch neu aggregiert.
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="mb-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <ComparisonCard label="Kosten" current={currentTotals.cost_eur} previous={previousTotals.cost_eur} formatter={(n) => fmtEur(n)} lowerIsBetter={false} neutralDirection />
        <ComparisonCard label="Klicks" current={currentTotals.clicks} previous={previousTotals.clicks} formatter={(n) => fmtNumber(Math.round(n))} />
        <ComparisonCard label="Impressionen" current={currentTotals.impressions} previous={previousTotals.impressions} formatter={(n) => fmtNumber(Math.round(n))} />
        <ComparisonCard label="Conversions" current={currentTotals.conversions} previous={previousTotals.conversions} formatter={(n) => fmtNumber(Math.round(n))} tonePositive />
        <ComparisonCard label="CPA" current={currentTotals.cpa_eur} previous={previousTotals.cpa_eur} formatter={(n) => fmtEur(n)} lowerIsBetter cpaNullNote />
        <ComparisonCard label="Conversion Rate" current={currentTotals.conv_rate_pct} previous={previousTotals.conv_rate_pct} formatter={(n) => fmtPct(n)} percentagePoints />
        <ComparisonCard label="Search Impression Share" current={currentTotals.sis} previous={previousTotals.sis} formatter={(n) => fmtPct(n)} percentagePoints />
      </div>

      <Card className="mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Interpretation</CardTitle>
          <CardDescription>
            Aktuell: <strong>{currentLabel}</strong> · Vergleich: <strong>{previousLabel}</strong> · Kampagne:{" "}
            <strong>{campaign}</strong>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <PeriodInterpretation cur={currentTotals} prev={previousTotals} />
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title="Conversions im Zeitverlauf">
          <LineOverTime data={rowsForChart} dataKey="conv" color="hsl(142 71% 40%)" />
        </ChartCard>
        <ChartCard title="CPA im Zeitverlauf" note="CPA nicht verfügbar bei 0 Conversions">
          <LineOverTime data={rowsForChart} dataKey="cpa" color="hsl(215 90% 45%)" euro />
        </ChartCard>
        <ChartCard title="Kosten im Zeitverlauf">
          <LineOverTime data={rowsForChart} dataKey="cost" color="hsl(215 20% 45%)" euro />
        </ChartCard>
        <ChartCard title="Conversion Rate im Zeitverlauf">
          <LineOverTime data={rowsForChart} dataKey="cr" color="hsl(142 71% 40%)" percent />
        </ChartCard>
        <ChartCard title="Search Impression Share im Zeitverlauf">
          <LineOverTime data={rowsForChart} dataKey="sis" color="hsl(260 60% 50%)" percent />
        </ChartCard>
      </div>
    </section>
  );
}

function ComparisonCard({
  label,
  current,
  previous,
  formatter,
  lowerIsBetter = false,
  percentagePoints = false,
  tonePositive = false,
  neutralDirection = false,
  cpaNullNote = false,
}: {
  label: string;
  current: number | null;
  previous: number | null;
  formatter: (n: number) => string;
  lowerIsBetter?: boolean;
  percentagePoints?: boolean;
  tonePositive?: boolean;
  neutralDirection?: boolean;
  cpaNullNote?: boolean;
}) {
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="mt-1 flex items-baseline gap-2">
          <div className={`text-2xl font-semibold tabular-nums ${tonePositive && (current ?? 0) > 0 ? "text-emerald-700" : ""}`}>
            {current === null ? "—" : formatter(current)}
          </div>
        </div>
        <div className="mt-1 text-xs text-muted-foreground">Vergleich: {previous === null ? "—" : formatter(previous)}</div>
        <div className="mt-2">
          {neutralDirection ? (
            <NeutralDelta current={current} previous={previous} formatter={formatter} />
          ) : (
            <TrendPill current={current} previous={previous} lowerIsBetter={lowerIsBetter} isPercentagePoints={percentagePoints} formatter={formatter} />
          )}
        </div>
        {cpaNullNote && current === null ? (
          <div className="mt-1 text-[11px] italic text-muted-foreground">CPA nicht verfügbar bei 0 Conversions</div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function NeutralDelta({ current, previous, formatter }: { current: number | null; previous: number | null; formatter: (n: number) => string }) {
  if (current === null || previous === null || previous === 0) return <span className="text-xs text-muted-foreground">—</span>;
  const diff = current - previous;
  const pct = (diff / Math.abs(previous)) * 100;
  const Icon = diff === 0 ? ArrowRight : diff > 0 ? ArrowUp : ArrowDown;
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
      <Icon className="h-3 w-3" />
      {diff > 0 ? "+" : ""}{formatter(diff)} <span>({pct > 0 ? "+" : ""}{pct.toLocaleString("de-DE", { maximumFractionDigits: 1 })}%)</span>
    </span>
  );
}

function PeriodInterpretation({
  cur,
  prev,
}: {
  cur: { conversions: number; cpa_eur: number | null; conv_rate_pct: number; sis: number | null };
  prev: { conversions: number; cpa_eur: number | null; conv_rate_pct: number; sis: number | null };
}) {
  const items: { icon: React.ReactNode; text: string; tone: "positive" | "warning" | "neutral" }[] = [];
  if (prev.conversions === 0 && cur.conversions > 0) {
    items.push({ icon: <TrendingUp className="h-4 w-4 text-emerald-600" />, text: `Conversions: von 0 auf ${cur.conversions} gestiegen.`, tone: "positive" });
  } else if (cur.conversions !== prev.conversions) {
    const better = cur.conversions > prev.conversions;
    items.push({
      icon: better ? <TrendingUp className="h-4 w-4 text-emerald-600" /> : <TrendingDown className="h-4 w-4 text-red-600" />,
      text: `Conversions ${better ? "höher" : "niedriger"} (${prev.conversions} → ${cur.conversions}).`,
      tone: better ? "positive" : "warning",
    });
  }
  if (cur.cpa_eur !== null && prev.cpa_eur !== null) {
    const better = cur.cpa_eur < prev.cpa_eur;
    items.push({
      icon: better ? <TrendingDown className="h-4 w-4 text-emerald-600" /> : <TrendingUp className="h-4 w-4 text-red-600" />,
      text: `CPA ${better ? "gesunken" : "gestiegen"} (${fmtEur(prev.cpa_eur)} → ${fmtEur(cur.cpa_eur)}). Niedriger ist besser.`,
      tone: better ? "positive" : "warning",
    });
  } else if (cur.cpa_eur === null) {
    items.push({ icon: <Info className="h-4 w-4 text-muted-foreground" />, text: "CPA aktuell nicht verfügbar bei 0 Conversions.", tone: "neutral" });
  }
  if (cur.conv_rate_pct !== prev.conv_rate_pct) {
    const better = cur.conv_rate_pct > prev.conv_rate_pct;
    items.push({
      icon: better ? <TrendingUp className="h-4 w-4 text-emerald-600" /> : <TrendingDown className="h-4 w-4 text-red-600" />,
      text: `Conversion Rate ${better ? "höher" : "niedriger"} (${fmtPct(prev.conv_rate_pct)} → ${fmtPct(cur.conv_rate_pct)}).`,
      tone: better ? "positive" : "warning",
    });
  }
  if (cur.sis !== null && prev.sis !== null) {
    const diff = cur.sis - prev.sis;
    if (Math.abs(diff) > 0.5) {
      const better = diff > 0;
      items.push({
        icon: better ? <TrendingUp className="h-4 w-4 text-emerald-600" /> : <TrendingDown className="h-4 w-4 text-amber-600" />,
        text: `Search Impression Share ${better ? "höher" : "niedriger"} (${fmtPct(prev.sis)} → ${fmtPct(cur.sis)}).`,
        tone: better ? "positive" : "warning",
      });
    }
  }
  if (items.length === 0) return <p className="text-sm text-muted-foreground">Keine wesentlichen Änderungen im Vergleich.</p>;
  return (
    <ul className="space-y-2">
      {items.map((i, idx) => (
        <li key={idx} className="flex items-start gap-2 text-sm">
          <span className="mt-0.5">{i.icon}</span>
          <span>{i.text}</span>
        </li>
      ))}
    </ul>
  );
}

function ChartCard({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
        {note ? <CardDescription>{note}</CardDescription> : null}
      </CardHeader>
      <CardContent>
        <div className="h-64 w-full">{children}</div>
      </CardContent>
    </Card>
  );
}

interface ChartRow {
  label: string;
  period_start: string;
  is_partial: boolean;
  cost: number;
  conv: number;
  cpa: number | null;
  cr: number;
  sis: number | null;
  lostB: number | null;
  lostR: number | null;
}

function LineOverTime({
  data,
  dataKey,
  color,
  euro,
  percent,
  twoLines,
  lineA,
  lineB,
}: {
  data: ChartRow[];
  dataKey?: keyof ChartRow;
  color?: string;
  euro?: boolean;
  percent?: boolean;
  twoLines?: boolean;
  lineA?: { key: keyof ChartRow; color: string; label: string };
  lineB?: { key: keyof ChartRow; color: string; label: string };
}) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(215 15% 90%)" />
        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
        <YAxis
          tick={{ fontSize: 11 }}
          tickFormatter={(v) => (euro ? `${v.toLocaleString("de-DE")} €` : percent ? `${v}%` : `${v}`)}
        />
        <Tooltip
          formatter={(value: unknown, name: string) => {
            if (value === null || value === undefined) return ["—", name];
            const v = value as number;
            return [euro ? fmtEur(v) : percent ? fmtPct(v) : fmtNumber(Math.round(v)), name];
          }}
          labelFormatter={(l, payload) => {
            const item = payload?.[0]?.payload as ChartRow | undefined;
            return `${l}${item?.is_partial ? " · Teilzeitraum" : ""}`;
          }}
        />
        {twoLines && lineA && lineB ? (
          <>
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey={lineA.key as string} name={lineA.label} stroke={lineA.color} strokeWidth={2} dot={{ r: 3 }} connectNulls />
            <Line type="monotone" dataKey={lineB.key as string} name={lineB.label} stroke={lineB.color} strokeWidth={2} dot={{ r: 3 }} connectNulls />
          </>
        ) : (
          <Line type="monotone" dataKey={dataKey as string} stroke={color} strokeWidth={2} dot={{ r: 3 }} connectNulls />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
}

// ---------- Section 3: Monthly ----------

function MonthlyComparison() {
  // With only aggregated CSVs, one period crosses May/June (2026-05-31 – 2026-06-06).
  // Show partial monthly aggregation with warning.
  const may = aggregateTotals(TOTAL_PERIODS.filter((p) => p.period_start >= "2026-05-05" && p.period_end <= "2026-05-30"));
  const june = aggregateTotals(TOTAL_PERIODS.filter((p) => !p.is_partial && p.period_start >= "2026-06-07" && p.period_end <= "2026-06-27"));
  const juneWithPartial = aggregateTotals(TOTAL_PERIODS.filter((p) => p.period_start >= "2026-06-07"));
  return (
    <section id="monthly">
      <SectionHeader title="Monatsvergleich" subtitle="Vergleich Mai vs. Juni. Exakte Monatswerte erst nach Import von Tagesdaten möglich." />
      <Alert className="mb-4 border-amber-200 bg-amber-50/60">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertTitle>Exakte Monatswerte nur mit Tagesdaten</AlertTitle>
        <AlertDescription className="text-sm">
          Monatswerte erst nach Import von Tagesdaten exakt verfügbar, da einzelne Google-Ads-Exporte
          monatsübergreifend aggregiert sind (z.&nbsp;B. 31.05.–06.06.). Die untenstehenden Werte
          approximieren die Monate anhand vollständiger CSV-Zeiträume.
        </AlertDescription>
      </Alert>
      <div className="grid gap-4 md:grid-cols-2">
        <MonthCard title="Mai 2026 (ca.)" period="05.05. – 30.05.2026" totals={may} note="CSV-Zeiträume, nicht monatsgenau" />
        <MonthCard title="Juni 2026 (ca.)" period="07.06. – 27.06.2026 · zzgl. Teilzeitraum 28.–30.06." totals={june} additionalTotals={juneWithPartial} note="Vollständige Wochen; Teilzeitraum separat ausgewiesen" />
      </div>
    </section>
  );
}

function MonthCard({
  title,
  period,
  totals,
  additionalTotals,
  note,
}: {
  title: string;
  period: string;
  totals: ReturnType<typeof aggregateTotals>;
  additionalTotals?: ReturnType<typeof aggregateTotals>;
  note?: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{period}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <MiniStat label="Kosten" value={fmtEur(totals.cost_eur)} />
          <MiniStat label="Klicks" value={fmtNumber(totals.clicks)} />
          <MiniStat label="Impr." value={fmtNumber(totals.impressions)} />
          <MiniStat label="Conversions" value={fmtNumber(totals.conversions)} />
          <MiniStat label="CPA" value={fmtEur(totals.cpa_eur)} />
          <MiniStat label="Conv.-Rate" value={fmtPct(totals.conv_rate_pct)} />
        </div>
        {additionalTotals ? (
          <div className="mt-3 rounded-md bg-muted/50 p-2 text-xs text-muted-foreground">
            Inkl. Teilzeitraum: Kosten {fmtEur(additionalTotals.cost_eur)}, Klicks {fmtNumber(additionalTotals.clicks)}, Conv. {fmtNumber(additionalTotals.conversions)}, CPA {fmtEur(additionalTotals.cpa_eur)}.
          </div>
        ) : null}
        {note ? <div className="mt-2 text-xs italic text-muted-foreground">{note}</div> : null}
      </CardContent>
    </Card>
  );
}

// ---------- Section 4: Campaign Comparison ----------

function CampaignComparison() {
  const rows = CAMPAIGN_PERIODS.filter((r) => !r.is_partial);
  const industrie = aggregateTotals(rows.filter((r) => r.campaign === "Industriebodenbeschichtung"));
  const tiefgarage = aggregateTotals(rows.filter((r) => r.campaign === "Tiefgarage"));
  const industrieSIS = weightedImprShare(rows.filter((r) => r.campaign === "Industriebodenbeschichtung"));
  const tiefgarageSIS = weightedImprShare(rows.filter((r) => r.campaign === "Tiefgarage"));

  const chartData = [
    { name: "Kosten (€)", Industrie: industrie.cost_eur, Tiefgarage: tiefgarage.cost_eur },
    { name: "Klicks", Industrie: industrie.clicks, Tiefgarage: tiefgarage.clicks },
    { name: "Conversions", Industrie: industrie.conversions, Tiefgarage: tiefgarage.conversions },
  ];

  return (
    <section id="campaigns">
      <SectionHeader title="Kampagnen-Vergleich" subtitle="Industriebodenbeschichtung vs. Tiefgarage (vollständiger Seed-Zeitraum 05.05.–27.06.)." />

      <Alert className="mb-4 border-emerald-200 bg-emerald-50/60">
        <Trophy className="h-4 w-4 text-emerald-600" />
        <AlertTitle className="text-emerald-900">Erkenntnis</AlertTitle>
        <AlertDescription className="text-sm text-emerald-950">
          Industriebodenbeschichtung generiert alle Leads. Tiefgarage hat trotz Spend 0 Leads generiert.
          Das Budget sollte aktuell auf Industriebodenbeschichtung konzentriert werden.
        </AlertDescription>
      </Alert>

      <div className="mb-4 grid gap-4 md:grid-cols-2">
        <CampaignCard
          name="Industriebodenbeschichtung"
          badge={<Badge className="bg-emerald-100 text-emerald-800"><Trophy className="mr-1 h-3 w-3" />Lead-generierende Kampagne</Badge>}
          totals={industrie}
          sis={industrieSIS}
          borderClass="border-emerald-200"
        />
        <CampaignCard
          name="Tiefgarage"
          badge={<Badge className="bg-red-100 text-red-800"><Ban className="mr-1 h-3 w-3" />0 Leads – Budget aktuell nicht empfohlen</Badge>}
          totals={tiefgarage}
          sis={tiefgarageSIS}
          borderClass="border-red-200"
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Direktvergleich</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(215 15% 90%)" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: unknown) => fmtNumber(Math.round(v as number))} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Industrie" name="Industriebodenbeschichtung" fill={CAMPAIGN_COLORS.Industriebodenbeschichtung} />
                <Bar dataKey="Tiefgarage" name="Tiefgarage" fill={CAMPAIGN_COLORS.Tiefgarage} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

function CampaignCard({
  name,
  badge,
  totals,
  sis,
  borderClass,
}: {
  name: string;
  badge: React.ReactNode;
  totals: ReturnType<typeof aggregateTotals>;
  sis: number | null;
  borderClass: string;
}) {
  return (
    <Card className={borderClass}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base">{name}</CardTitle>
          {badge}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <MiniStat label="Spend" value={fmtEur(totals.cost_eur)} />
          <MiniStat label="Klicks" value={fmtNumber(totals.clicks)} />
          <MiniStat label="Conversions" value={fmtNumber(totals.conversions)} />
          <MiniStat label="CPA" value={fmtEur(totals.cpa_eur)} />
          <MiniStat label="Conv.-Rate" value={fmtPct(totals.conv_rate_pct)} />
          <MiniStat label="Search IS" value={fmtPct(sis)} />
        </div>
      </CardContent>
    </Card>
  );
}

// ---------- Section 5: Auction Insights ----------

function AuctionInsightsSection() {
  const [showDetails, setShowDetails] = useState(false);
  return (
    <section id="auction">
      <SectionHeader title="Auction Insights" subtitle="Sichtbarkeit im Auktionsvergleich zu Wettbewerbern." />
      <Alert className="mb-4 border-emerald-200 bg-emerald-50/60">
        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
        <AlertTitle className="text-emerald-900">Wettbewerbsposition</AlertTitle>
        <AlertDescription className="text-sm text-emerald-950">
          Die Sichtbarkeit im Auktionsvergleich ist gut: <strong>Schneider (You)</strong> erreicht{" "}
          <strong>36,90 %</strong> Impression Share und liegt damit vor den aufgeführten Wettbewerbern.
          Die wichtigsten sichtbaren Wettbewerber sind <strong>epodex.com</strong>, <strong>koch-industrieboeden.de</strong>{" "}
          und <strong>mr-epoxy.de</strong>. Bei Top-of-page und Absolute-top-of-page gibt es weiterhin
          Optimierungspotenzial, aber die grundsätzliche Wettbewerbsposition ist stark.
        </AlertDescription>
      </Alert>
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <KpiCard label="Impression Share (You)" value="36,90%" tone="positive" />
        <KpiCard label="Top of Page (You)" value="75,47%" />
        <KpiCard label="Absolute Top of Page (You)" value="42,53%" hint="Optimierungspotenzial" tone="warning" />
      </div>
      <Card className="mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Wichtigste Wettbewerber</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 sm:grid-cols-3">
            {AUCTION_INSIGHTS.filter((r) => r.domain !== "Schneider (You)").slice(0, 3).map((r) => (
              <div key={r.domain} className="rounded-md border p-3">
                <div className="text-sm font-semibold">{r.domain}</div>
                <div className="text-xs text-muted-foreground">Impression Share: {r.impression_share}</div>
                <div className="text-xs text-muted-foreground">Overlap: {r.overlap_rate}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Button variant="outline" size="sm" onClick={() => setShowDetails((s) => !s)}>
        {showDetails ? <ChevronUp className="mr-1 h-4 w-4" /> : <ChevronDown className="mr-1 h-4 w-4" />}
        Details anzeigen
      </Button>
      {showDetails ? (
        <Card className="mt-3">
          <CardContent className="overflow-x-auto pt-5">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Domain</TableHead>
                  <TableHead className="text-right">Impr. Share</TableHead>
                  <TableHead className="text-right">Overlap</TableHead>
                  <TableHead className="text-right">Position Above</TableHead>
                  <TableHead className="text-right">Top of Page</TableHead>
                  <TableHead className="text-right">Abs. Top of Page</TableHead>
                  <TableHead className="text-right">Outranking</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {AUCTION_INSIGHTS.map((r) => (
                  <TableRow key={r.domain} className={r.domain === "Schneider (You)" ? "bg-emerald-50/60 font-semibold" : ""}>
                    <TableCell>{r.domain}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.impression_share}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.overlap_rate ?? "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.position_above_rate ?? "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.top_of_page_rate}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.absolute_top_of_page_rate}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.outranking_share ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : null}
    </section>
  );
}

// ---------- Root ----------

function ReportingDashboard() {
  return (
    <div className="min-h-screen bg-muted/30">
      {/* Branded header — Schneider red primary */}
      <header className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-7xl px-4 py-10 md:py-14">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-5">
              <div className="rounded-md bg-white p-2 shadow-sm">
                <img
                  src={logoImg}
                  alt="Schneider Bodenbeschichtungen Logo"
                  className="h-12 w-auto md:h-14"
                />
              </div>
              <div>
                <div className="text-xs font-medium uppercase tracking-widest text-primary-foreground/80">
                  Google Ads Reporting
                </div>
                <h1 className="mt-1 text-2xl font-bold tracking-tight md:text-3xl">
                  Owner-Dashboard
                </h1>
                <p className="mt-1 text-sm text-primary-foreground/80">
                  Schneider Bodenbeschichtungen · Berichtszeitraum 05.05.2026 – 30.06.2026
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Section navigation */}
      <div className="border-b bg-background">
        <div className="mx-auto max-w-7xl px-4 py-3">
          <nav className="flex flex-wrap gap-2 text-xs">
            {[
              ["summary", "Executive Summary"],
              ["periods", "Wochenvergleich"],
              ["monthly", "Monatsvergleich"],
              ["campaigns", "Kampagnen"],
              ["auction", "Auction Insights"],
            ].map(([id, label]) => (
              <a
                key={id}
                href={`#${id}`}
                className="rounded-md border bg-background px-3 py-1.5 font-medium hover:border-primary hover:text-primary"
              >
                {label}
              </a>
            ))}
          </nav>
        </div>
      </div>

      <main className="mx-auto max-w-7xl space-y-10 px-4 py-8">
        <ExecutiveSummary />

        <Alert className="border-primary/40 bg-primary/5">
          <Target className="h-4 w-4 text-primary" />
          <AlertTitle className="text-primary">Empfehlung</AlertTitle>
          <AlertDescription className="text-sm">
            Budget aktuell auf <strong>Industriebodenbeschichtung</strong> fokussieren. Tiefgarage hat im
            bisherigen Zeitraum <strong>0 Leads</strong> generiert, während Industriebodenbeschichtung alle
            Conversions liefert. <em>Tiefgarage aktuell nicht weiter budgetieren.</em>
          </AlertDescription>
        </Alert>

        <PeriodComparison />
        <MonthlyComparison />
        <CampaignComparison />
        <AuctionInsightsSection />
      </main>

      <footer className="border-t bg-background py-6">
        <div className="mx-auto max-w-7xl px-4 text-xs text-muted-foreground">
          Reporting basiert auf CSV-Zeiträumen des Google-Ads-Exports. Nach Import täglicher Daten
          werden Wochen und Monate exakt aggregiert (Mo 00:00 CET/CEST bis nächsten Mo 00:00 exklusiv).
        </div>
      </footer>
    </div>
  );
}
